import { decodeBase64 } from 'support/generalUtils';

export type JwtClaims = {
  sub: string;
  iat: number; // seconds epoch
  exp: number; // seconds epoch
};

type SignJwtParams = {
  claims: JwtClaims;
  keyId: string;
  privateKey: string; // base64 encoded PKCS#8 PEM
};

const PEM_ARMOR = /-----(BEGIN|END) PRIVATE KEY-----/g;
const WHITESPACE = /\s+/g;

const RS256_ALGORITHM = {
  name: 'RSASSA-PKCS1-v1_5',
  hash: { name: 'SHA-256' },
};

const bytesToBinary = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return binary;
};

const binaryToBuffer = (binary: string): ArrayBuffer => {
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const base64UrlEncode = (bytes: Uint8Array): string =>
  btoa(bytesToBinary(bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const encodeJson = (value: object): string =>
  base64UrlEncode(new TextEncoder().encode(JSON.stringify(value)));

/**
 * The config ships the PEM itself base64 encoded, so it is decoded twice: once
 * to recover the PEM text, and again to turn the PEM body into DER bytes.
 */
const pemToDer = (privateKey: string): ArrayBuffer => {
  const pem = decodeBase64(privateKey);
  const body = pem.replace(PEM_ARMOR, '').replace(WHITESPACE, '');

  return binaryToBuffer(decodeBase64(body));
};

/**
 * Signs a RS256 JWT with WebCrypto. `crypto.subtle` only exists in a secure
 * context, so this rejects rather than throws when it is unavailable.
 */
export const signJwt = async (params: SignJwtParams): Promise<string> => {
  const { claims, keyId, privateKey } = params;

  const subtle = window.crypto?.subtle;
  if (!subtle) throw new Error('crypto.subtle is unavailable');

  const key = await subtle.importKey(
    'pkcs8',
    pemToDer(privateKey),
    RS256_ALGORITHM,
    false,
    ['sign'],
  );

  const header = { alg: 'RS256', typ: 'JWT', kid: keyId };
  const signingInput = `${encodeJson(header)}.${encodeJson(claims)}`;

  const signature = await subtle.sign(
    RS256_ALGORITHM,
    key,
    new TextEncoder().encode(signingInput),
  );

  return `${signingInput}.${base64UrlEncode(new Uint8Array(signature))}`;
};
